import { memo, useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Controller, useForm } from "react-hook-form";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { CloseIcon } from "../../assets/icons/close";
import { BsArrowRight } from "react-icons/bs";
import { activateNotification } from "../../utils/notifications-utils";
import { CONTACT_MODAL_TYPES, ContactUsContext } from "../../providers/contactUs.provider";
import { CustomModal } from "./contactModal.styles";
import { useApi } from "../../hooks/api.hook";
import { isValidNigerianPhone } from "../../utils/validate-phone-util";
import { ActionSelect, ActionSelectOption } from "../actionSelect";
import { Mixpanel } from "../../utils/mixpanel";

export const ContactModal = memo(() => {
  const { closeContactModal, message, modalType, setMessage, isOpen } = useContext(ContactUsContext);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const isBusinessPricing = modalType === CONTACT_MODAL_TYPES.BUSINESS_PRICING;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      surname: message?.surname ?? "",
      lastname: message?.lastname ?? "",
      email: message?.email ?? "",
      phonenumber: message?.phonenumber ?? "",
      message: message?.message ?? "",
      businessName: "",
      fullName: "",
      contactPerson: "",
      businessPhone: "",
      whatsAppNumber: "",
      whatsAppSameAsPhone: false,
      businessEmail: "",
      businessLocation: "",
      businessType: "",
      otherBusinessType: "",
      weeklyVolume: "",
      produceItems: [],
      otherProduceItems: "",
      consultationDateTime: null,
      understandsConsultation: false,
    },
  });
  const selectedBusinessType = watch("businessType");
  const businessPhone = watch("businessPhone");
  const whatsAppSameAsPhone = watch("whatsAppSameAsPhone");
  const selectedProduceItems = watch("produceItems") || [];
  const isOtherProduce = selectedProduceItems.includes("Other");

  useEffect(() => {
    if (isOpen) {
      if (isBusinessPricing) {
        Mixpanel.track("View-B2BPriceRequestModal")
      }
      else {
        Mixpanel.track("View-ContactUsModal")
      }
    }
  }, [isOpen])
  useEffect(() => {
    if (whatsAppSameAsPhone) {
      setValue("whatsAppNumber", businessPhone || "", { shouldValidate: true });
    }
  }, [businessPhone, setValue, whatsAppSameAsPhone]);

  useEffect(() => {
    if (isOpen) {
      reset({
        surname: message?.surname ?? "",
        lastname: message?.lastname ?? "",
        email: message?.email ?? "",
        phonenumber: message?.phonenumber ?? "",
        message: message?.message ?? "",
        businessName: "",
        fullName: "",
        contactPerson: "",
        businessPhone: "",
        whatsAppNumber: "",
        whatsAppSameAsPhone: false,
        businessEmail: "",
        businessLocation: "",
        businessType: "",
        otherBusinessType: "",
        weeklyVolume: "",
        produceItems: [],
        otherProduceItems: "",
        consultationDateTime: null,
        understandsConsultation: false,
      });
    }
  }, [isOpen, message, modalType, reset]);

  const onSubmit = async (formData) => {
    setLoading(true);
    const payload = isBusinessPricing
      ? {
        fullName: formData.fullName,
        businessName: formData.businessName,
        phoneNumber: formData.businessPhone,
        whatsAppNumber: formData.whatsAppNumber,
        emailAddress: formData.businessEmail,
        businessLocation: formData.businessLocation,
        businessType: formData.businessType,
        otherBusinessType: formData.businessType === "Other" ? formData.otherBusinessType : "",
        operatingYear: "N/A",
        averageWeeklyCustomerVolume: formData.weeklyVolume || "N/A",
        produceItems: (formData.produceItems || []).filter((item) => item !== "Other"),
        otherProduceItems: formData.otherProduceItems || "",
        weeklyProduceBudget: "N/A",
        restockDuration: "N/A",
        supplyChallenge: "N/A",
        whatAreYouLookingFor: "N/A",
        whenDoYouWantToStart: "N/A",
        consultationDate: formData.consultationDateTime ? `${formData.consultationDateTime.getFullYear()}-${String(formData.consultationDateTime.getMonth() + 1).padStart(2, "0")}-${String(formData.consultationDateTime.getDate()).padStart(2, "0")}` : "",
        consultationTime: formData.consultationDateTime ? `${String(formData.consultationDateTime.getHours()).padStart(2, "0")}:${String(formData.consultationDateTime.getMinutes()).padStart(2, "0")}` : "",
      }
      : {
        firstName: formData.surname,
        lastName: formData.lastname,
        phoneNumber: formData.phonenumber,
        emailAddress: formData.email,
        message: formData.message,
      };
    try {
      await api.post(isBusinessPricing ? "/contact-form/b2b-enquiry" : "/contact-form/general-enquiry", payload);
      setMessage(formData);
      activateNotification({
        title: "Success",
        body: isBusinessPricing
          ? "Your B2B pricing request has been sent."
          : "Message sent. We'll provide feedback to your email.",
      });
      if (isBusinessPricing) {
        Mixpanel.track_success_b2b_price_request_submitted(formData)
      } else {
        Mixpanel.track_success_contact_form_submitted({ ...payload, mobile: payload?.phoneNumber, email: payload?.emailAddress })
      }
      closeContactModal();
    } catch (err) {
      const messageText =
        err?.response?.data?.message || "An error occurred";
      activateNotification({
        title: "Error",
        body: messageText,
      });
      if (isBusinessPricing) {
        Mixpanel.track_failed_b2b_price_request_submitted(formData)
      } else {
        Mixpanel.track_failed_contact_form_submitted({ ...payload, mobile: payload?.phoneNumber, email: payload?.emailAddress })
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal isOpen={isOpen} toggle={closeContactModal} className={isBusinessPricing ? "business-pricing-modal" : "contact-us-modal"}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={closeContactModal}>
          {isBusinessPricing ?
            <>
              <span className="title business-pricing-title">Get B2B pricing for your <em className="secondary">business</em></span>
              <span className="subtitle business-pricing-subtitle">No prior commitment — an account officer will call you back with this week’s locked rates.</span>
            </> :
            <>
              <span className="title contact-us-title">Love to hear from you. <em className="primary">Get in touch!</em></span>
              <span className="subtitle contact-us-subtitle">No prior commitment — an account officer will call you back with this week’s locked rates.</span>
            </>
          }
          <div className="close-btn" onClick={closeContactModal}>
            <CloseIcon />
          </div>
        </ModalHeader>
        <ModalBody>
          {isBusinessPricing ? <>
            <Row className="business-pricing-fields">
              <Col md="6"><FormGroup><Label for="full-name">Full Name<span>*</span></Label><Controller name="fullName" control={control} rules={{ required: "Full name is required" }} render={({ field }) => <Input {...field} id="full-name" placeholder="Enter your full name" invalid={!!errors.fullName} />} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label for="business-name">Business Name<span>*</span></Label><Controller name="businessName" control={control} rules={{ required: "Business name is required" }} render={({ field }) => <Input {...field} id="business-name" placeholder="Enter your business name" invalid={!!errors.businessName} />} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label for="business-phone">Phone Number<span>*</span></Label><Controller name="businessPhone" control={control} rules={{ required: "Phone number is required", validate: isValidNigerianPhone }} render={({ field }) => <Input {...field} id="business-phone" type="tel" placeholder="E.g. 0801 234 5678" invalid={!!errors.businessPhone} />} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label for="whatsapp-number">WhatsApp Number</Label><Controller name="whatsAppNumber" control={control} rules={{ validate: (value) => !value || isValidNigerianPhone(value) }} render={({ field }) => <Input {...field} id="whatsapp-number" type="tel" placeholder="E.g. 0801 234 5678" disabled={whatsAppSameAsPhone} invalid={!!errors.whatsAppNumber} />} /><Controller name="whatsAppSameAsPhone" control={control} render={({ field }) => <label className="same-as-phone-check"><input type="checkbox" checked={field.value} onChange={(event) => { field.onChange(event.target.checked); if (event.target.checked) setValue("whatsAppNumber", businessPhone || "", { shouldValidate: true }); }} />Same as phone number</label>} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label for="business-email">Email Address</Label><Controller name="businessEmail" control={control} rules={{ pattern: { value: /^\S+@\S+\.\S+$/, message: "This email address is not valid" } }} render={({ field }) => <Input {...field} id="business-email" type="email" placeholder="Your@business.com" invalid={!!errors.businessEmail} />} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label for="business-location">Business Location<span>*</span></Label><Controller name="businessLocation" control={control} rules={{ required: "Business location is required" }} render={({ field }) => <Input {...field} id="business-location" placeholder="Enter business location" invalid={!!errors.businessLocation} />} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label>Business Type<span>*</span></Label><Controller name="businessType" control={control} rules={{ required: "Business type is required" }} render={({ field }) => <ActionSelect className={`business-type-select ${errors.businessType ? "is-invalid" : ""}`} isPlaceholder={!field.value} label={field.value || "Select your business type"}>{["Restaurant", "Buka / Food Vendor", "Caterer", "Hotel", "Event Kitchen", "Food Production Business", "Other"].map((type) => <ActionSelectOption key={type} onSelect={() => field.onChange(type)}>{type}</ActionSelectOption>)}</ActionSelect>} /></FormGroup></Col>
              {selectedBusinessType === "Other" && <Col md="6"><FormGroup><Label for="other-business-type">Other Business Type<span>*</span></Label><Controller name="otherBusinessType" control={control} rules={{ required: "Please specify your business type" }} render={({ field }) => <Input {...field} id="other-business-type" placeholder="Specify your business type" invalid={!!errors.otherBusinessType} />} /></FormGroup></Col>}
              <Col md="6"><FormGroup><Label for="weekly-volume">Estimated Weekly Volume</Label><Controller name="weeklyVolume" control={control} render={({ field }) => <Input {...field} id="weekly-volume" placeholder="E.g. 200kg Or 10 Crates" />} /></FormGroup></Col>
            </Row>
            <Row className="business-pricing-extra-fields tw-items-center">
              <Col md="6">
                <FormGroup className="business-pricing-extra-field">
                  <Label>What produce items do you frequently purchase? (multiple selection)<span>*</span></Label>
                  <ActionSelect
                    className="business-produce-select"
                    label={selectedProduceItems.length ? `${selectedProduceItems.length} produce items selected` : "Select produce items"}
                    isPlaceholder={!selectedProduceItems.length}
                    closeOnSelect={false}
                  >
                    {["Tubers (Yam, Potato)", "Stew package (Pepper, Tomato, Onions, Tatashe)", "Protein (Cow, Goat, Snails)", "Other"].map((item) => (
                      <button
                        type="button"
                        className="market-checkbox-option"
                        key={item}
                        onClick={() => setValue(
                          "produceItems",
                          selectedProduceItems.includes(item)
                            ? selectedProduceItems.filter((value) => value !== item)
                            : [...selectedProduceItems, item],
                          { shouldValidate: true }
                        )}
                      >
                        <input type="checkbox" checked={selectedProduceItems.includes(item)} readOnly />
                        <span>{item}</span>
                      </button>
                    ))}
                  </ActionSelect>
                  <Controller name="produceItems" control={control} rules={{ validate: (value) => value?.length > 0 || "Please select produce items or specify other produce items" }} render={() => null} />
                </FormGroup>
              </Col>
              {isOtherProduce && <Col md="6"><FormGroup className="business-pricing-extra-field"><Label for="other-produce-items">Other Produce Items<span>*</span></Label><Controller name="otherProduceItems" control={control} rules={{ required: "Please specify other produce items" }} render={({ field }) => <Input {...field} id="other-produce-items" placeholder="Enter other produce items" invalid={!!errors.otherProduceItems} />} /></FormGroup></Col>}
              <Col md="6"><FormGroup className="business-pricing-extra-field"><Label for="consultation-date-time">Select preferred date & time<span>*</span></Label><Controller name="consultationDateTime" control={control} rules={{ required: "Please select a preferred date and time" }} render={({ field }) => <Flatpickr value={field.value || undefined} options={{ enableTime: true, minDate: "today", dateFormat: "d M Y, h:i K" }} className={`form-control ${errors.consultationDateTime ? "is-invalid" : ""}`} placeholder="Select preferred date & time" onChange={(dates) => field.onChange(dates[0] || null)} />} /></FormGroup></Col>
            </Row>
            <FormGroup className="business-consultation-check"><Controller name="understandsConsultation" control={control} rules={{ required: "Please confirm this is a business supply consultation" }} render={({ field }) => <label><input type="checkbox" checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />I understand this is a business supply consultation.</label>} /></FormGroup>
            <p className="business-pricing-help">*Please fill in the asterik and required fields to continue.</p>
          </> : <>
            <Row className="contact-us-fields">
              <Col md="6">
                <FormGroup>
                  <Label for="surname">First name<span>*</span></Label>
                  <Controller
                    name="surname"
                    control={control}
                    rules={{ required: "First name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="surname"
                        placeholder="Enter your first name"
                        invalid={!!errors.surname}
                      />
                    )}
                  />
                  {errors.surname?.message && (
                    <FormFeedback className="d-block">{errors.surname.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="lastname">Last name<span>*</span></Label>
                  <Controller
                    name="lastname"
                    control={control}
                    rules={{ required: "Last name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="lastname"
                        placeholder="Enter your last name"
                        invalid={!!errors.lastname}
                      />
                    )}
                  />
                  {errors.lastname?.message && (
                    <FormFeedback className="d-block">{errors.lastname.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="email">Email<span>*</span></Label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+\.\S+$/,
                        message: "This email address is not valid",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        invalid={!!errors.email}
                      />
                    )}
                  />
                  {errors.email?.message && <FormFeedback className="d-block">{errors.email.message}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="phonenumber">Phone Number <span>*</span></Label>
                  <Controller
                    name="phonenumber"
                    control={control}
                    rules={{ validate: isValidNigerianPhone }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="phonenumber"
                        type="tel"
                        placeholder="Enter your phone number"
                        invalid={!!errors.phonenumber}
                      />
                    )}
                  />
                  {errors.phonenumber?.message && <FormFeedback className="d-block">{errors.phonenumber.message}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col xs="12">
                <FormGroup>
                  <Label for="message">Message<span>*</span></Label>
                  <Controller
                    name="message"
                    control={control}
                    rules={{ required: "Message is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="message"
                        type="textarea"
                        rows="7"
                        invalid={!!errors.message}
                      />
                    )}
                  />
                  {errors.message?.message && <FormFeedback className="d-block">{errors.message.message}</FormFeedback>}
                </FormGroup>
              </Col>
            </Row>
          </>}
        </ModalBody>

        <ModalFooter>
          <Button type="submit" disabled={loading}>
            <span>{isBusinessPricing ? "Get B2B Pricing" : "Submit"}</span>
            {loading ? (
              <Spinner size="sm">Loading...</Spinner>
            ) : (
              <BsArrowRight size={25} />
            )}
          </Button>
        </ModalFooter>
      </Form>
    </CustomModal>
  );
});
